import { useId, useState } from 'react';

import { Info } from 'lucide-react';

import { mostrarAviso } from '@/modulos/operacao/comum/cadastroIndisponivel';
import { mascaraCpf } from '@/nucleo/formato/documentos';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';

/**
 * Não há tabela de substituição em `apps/api/src/modulos/` nem rota que a
 * receba: "Enviar solicitação" não pode fingir que a DTP recebeu o pedido.
 * Mesmo tom de `operacao/comum/cadastroIndisponivel.ts` (CLAUDE.md, "Regras
 * de interface", item 8).
 */
export const AVISO_DE_SUBSTITUICAO_INDISPONIVEL = {
  title: 'Solicitação de substituição ainda não disponível',
  description:
    'Estamos preparando esta funcionalidade. Em breve você poderá enviar o pedido de troca por aqui.',
} as const;

type TipoDeRecurso = 'CONDUTOR' | 'MONITOR' | 'VEICULO';

const TIPOS: { valor: TipoDeRecurso; rotulo: string }[] = [
  { valor: 'CONDUTOR', rotulo: 'Condutor' },
  { valor: 'MONITOR', rotulo: 'Monitor' },
  { valor: 'VEICULO', rotulo: 'Veículo' },
];

const MOTIVOS: { valor: string; rotulo: string }[] = [
  { valor: 'AFASTAMENTO_MEDICO', rotulo: 'Afastamento médico' },
  { valor: 'DEMISSAO', rotulo: 'Demissão (iniciativa do credenciado)' },
  { valor: 'PEDIDO_DEMISSAO', rotulo: 'Pedido de demissão (colaborador)' },
  { valor: 'APREENSAO_VEICULO', rotulo: 'Apreensão do veículo' },
  { valor: 'SINISTRO', rotulo: 'Sinistro' },
  { valor: 'MANUTENCAO', rotulo: 'Manutenção prolongada' },
  { valor: 'VENDA_VEICULO', rotulo: 'Venda do veículo' },
  { valor: 'OUTRO', rotulo: 'Outro' },
];

function hojeLocal(): string {
  const hoje = new Date();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  const dia = String(hoje.getDate()).padStart(2, '0');
  return `${hoje.getFullYear()}-${mes}-${dia}`;
}

interface Props {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
}

/**
 * "Solicitar substituição" do Portal do Credenciado, fiel a
 * `components/portal/SolicitarSubstituicaoDialog.tsx` do protótipo: mesmos
 * campos, rótulos, ordem e regra de habilitação do envio (nome atual, nome
 * novo e data de efeito preenchidos).
 *
 * Três diferenças, todas de conteúdo, nenhuma de layout:
 * - o envio mostra o aviso de indisponibilidade em vez de gravar, e o rodapé
 *   do formulário diz isso de forma fixa — aviso que some deixa quem audita
 *   uma captura de tela concluir que o pedido foi enviado;
 * - o CPF ganha máscara e limite de dígitos (no protótipo aceita texto livre);
 * - cada rótulo aponta para o seu campo (`htmlFor`), para o leitor de tela.
 */
export function SolicitarSubstituicaoDialog({ open, onOpenChange }: Props) {
  const id = useId();

  const [tipo, setTipo] = useState<TipoDeRecurso>('CONDUTOR');
  const [dataEfeito, setDataEfeito] = useState(hojeLocal);
  const [atualNome, setAtualNome] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [novoDoc, setNovoDoc] = useState('');
  const [motivo, setMotivo] = useState('AFASTAMENTO_MEDICO');
  const [detalhe, setDetalhe] = useState('');

  const ehVeiculo = tipo === 'VEICULO';
  const podeEnviar = Boolean(atualNome && novoNome && dataEfeito);

  const enviar = () => {
    mostrarAviso(AVISO_DE_SUBSTITUICAO_INDISPONIVEL);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Solicitar substituição"
      description="A troca só passa a valer após aprovação da DTP."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!podeEnviar}>
            Enviar solicitação
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            A DTP analisará a documentação. Você acompanhará o status em &quot;Minhas
            substituições&quot;.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`${id}-tipo`}>Tipo de recurso</Label>
            <Select value={tipo} onValueChange={(valor) => setTipo(valor as TipoDeRecurso)}>
              <SelectTrigger id={`${id}-tipo`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS.map((opcao) => (
                  <SelectItem key={opcao.valor} value={opcao.valor}>
                    {opcao.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={`${id}-data`}>Data de efeito desejada</Label>
            <Input
              id={`${id}-data`}
              type="date"
              value={dataEfeito}
              onChange={(evento) => setDataEfeito(evento.target.value)}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor={`${id}-atual`}>{ehVeiculo ? 'Placa atual' : 'Nome atual'}</Label>
          <Input
            id={`${id}-atual`}
            value={atualNome}
            onChange={(evento) => setAtualNome(evento.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`${id}-novo`}>{ehVeiculo ? 'Placa nova' : 'Nome novo'}</Label>
            <Input
              id={`${id}-novo`}
              value={novoNome}
              onChange={(evento) => setNovoNome(evento.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor={`${id}-doc`}>{ehVeiculo ? 'RENAVAM/Chassi' : 'CPF'}</Label>
            <Input
              id={`${id}-doc`}
              value={novoDoc}
              inputMode={ehVeiculo ? undefined : 'numeric'}
              maxLength={ehVeiculo ? undefined : 14}
              placeholder={ehVeiculo ? undefined : '000.000.000-00'}
              onChange={(evento) =>
                setNovoDoc(ehVeiculo ? evento.target.value : mascaraCpf(evento.target.value))
              }
            />
          </div>
        </div>

        <div>
          <Label htmlFor={`${id}-motivo`}>Motivo</Label>
          <Select value={motivo} onValueChange={setMotivo}>
            <SelectTrigger id={`${id}-motivo`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOTIVOS.map((opcao) => (
                <SelectItem key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor={`${id}-detalhe`}>Detalhe / observações</Label>
          <Textarea
            id={`${id}-detalhe`}
            value={detalhe}
            onChange={(evento) => setDetalhe(evento.target.value)}
            rows={2}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          O envio de solicitações ainda não está disponível: nada preenchido aqui é registrado nem
          chega à DTP.
        </p>
      </div>
    </ResponsiveModal>
  );
}
